if (!customElements.get('gift-options')) {
  customElements.define(
    'gift-options',
    class GiftOptions extends HTMLElement {
      connectedCallback() {
        this.giftToggle = this.querySelector('[data-gift-toggle]');
        this.choices = this.querySelector('[data-gift-choices]');
        this.wrap = this.querySelector('[data-gift-wrap]');
        this.card = this.querySelector('[data-greeting-card]');
        this.cardFields = this.querySelector('[data-card-fields]');
        this.message = this.querySelector('[data-gift-message]');
        this.sender = this.querySelector('[data-gift-sender]');
        this.counter = this.querySelector('[data-message-count]');
        this.summary = this.querySelector('[data-gift-summary]');
        this.total = this.querySelector('[data-gift-total]');
        this.status = this.querySelector('[data-gift-status]');
        this.saveTimer = null;

        if (this.dataset.hasMainProducts === 'false') {
          const hasOrphanedGiftState =
            this.dataset.hasGiftItems === 'true' || this.dataset.hasGiftAttributes === 'true';
          if (hasOrphanedGiftState && !window.Shopify?.designMode) {
            this.cleanupOrphanedGiftState();
          }
          return;
        }

        this.giftToggle?.addEventListener('change', () => this.onGiftToggle());
        this.wrap?.addEventListener('change', () => this.onOptionChange('wrap'));
        this.card?.addEventListener('change', () => this.onOptionChange('card'));
        this.message?.addEventListener('input', () => {
          this.counter.textContent = this.message.value.length;
          this.scheduleSave();
        });
        this.sender?.addEventListener('input', () => this.scheduleSave());
        this.updateSummary();
      }

      async cleanupOrphanedGiftState() {
        if (this.dataset.cleaning === 'true') return;
        this.dataset.cleaning = 'true';
        const updates = {};
        if (this.dataset.wrapVariantId) updates[this.dataset.wrapVariantId] = 0;
        if (this.dataset.cardVariantId) updates[this.dataset.cardVariantId] = 0;

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              updates,
              attributes: {
                'Gift order': '',
                'Gift wrapping': '',
                'Greeting card': '',
                'Gift message': '',
                'Gift sender': '',
              },
            }),
          });
          if (!response.ok) throw new Error('Unable to clean up gift options.');
          window.location.reload();
        } catch (error) {
          this.dataset.cleaning = 'false';
          console.error(error);
        }
      }

      async onGiftToggle() {
        const hadConnectedAddOns = Boolean(this.dataset.wrapVariantId || this.dataset.cardVariantId);
        this.choices.hidden = !this.giftToggle.checked;
        if (!this.giftToggle.checked) {
          if (this.wrap) this.wrap.checked = false;
          if (this.card) this.card.checked = false;
          if (this.cardFields) this.cardFields.hidden = true;
          await this.removeAddOn(this.dataset.wrapVariantId);
          await this.removeAddOn(this.dataset.cardVariantId);
        }
        this.updateSummary();
        await this.saveAttributes();
        if (!this.giftToggle.checked && hadConnectedAddOns) window.location.reload();
      }

      async onOptionChange(type) {
        const input = type === 'wrap' ? this.wrap : this.card;
        const variantId = type === 'wrap' ? this.dataset.wrapVariantId : this.dataset.cardVariantId;
        if (type === 'card' && this.cardFields) this.cardFields.hidden = !input.checked;

        if (variantId) {
          if (input.checked) {
            await this.addAddOn(variantId);
          } else {
            await this.removeAddOn(variantId);
          }
        }

        this.updateSummary();
        await this.saveAttributes();
        if (variantId) window.location.reload();
      }

      scheduleSave() {
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.saveAttributes(), 500);
      }

      async addAddOn(variantId) {
        this.setBusy(true);
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('Unable to add gift option.');
          this.status.textContent = 'Gift addition added.';
        } finally {
          this.setBusy(false);
        }
      }

      async removeAddOn(variantId) {
        if (!variantId) return;
        this.setBusy(true);
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: String(variantId), quantity: 0 }),
          });
          if (!response.ok) throw new Error('Unable to remove gift option.');
          this.status.textContent = 'Gift addition removed.';
        } finally {
          this.setBusy(false);
        }
      }

      async saveAttributes() {
        const isGift = Boolean(this.giftToggle?.checked);
        const attributes = {
          'Gift order': isGift ? 'Yes' : '',
          'Gift wrapping': isGift && this.wrap?.checked ? 'Yes' : '',
          'Greeting card': isGift && this.card?.checked ? 'Yes' : '',
          'Gift message': isGift && this.card?.checked ? this.message?.value.trim() || '' : '',
          'Gift sender': isGift && this.card?.checked ? this.sender?.value.trim() || '' : '',
        };

        this.setBusy(true);
        try {
          await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes }),
          });
          this.status.textContent = 'Gift choices saved.';
        } finally {
          this.setBusy(false);
        }
      }

      updateSummary() {
        let amount = 0;
        if (this.giftToggle?.checked && this.wrap?.checked) {
          amount += Number(this.dataset.wrapPreviewPrice || 0);
        }
        if (this.giftToggle?.checked && this.card?.checked) {
          amount += Number(this.dataset.cardPreviewPrice || 0);
        }
        if (this.summary) this.summary.hidden = amount === 0;
        if (this.total) this.total.textContent = this.formatMoney(amount);
      }

      formatMoney(cents) {
        if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
        return `₹${(cents / 100).toFixed(0)}`;
      }

      setBusy(isBusy) {
        this.toggleAttribute('aria-busy', isBusy);
      }
    }
  );
}
